import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { furnitureApi, furnitureTypesApi } from '../services/api';
import {
  ScanBarcode,
  Camera,
  X,
  Keyboard,
  Package,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Smartphone,
  Zap,
  RefreshCw,
  CameraOff,
  FlashlightOff,
  Flashlight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const STATUS_CONFIG = {
  neuf: { label: 'Neuf', class: 'bg-emerald-50 text-emerald-700' },
  bon: { label: 'Bon', class: 'bg-blue-50 text-blue-700' },
  usage: { label: 'Usagé', class: 'bg-amber-50 text-amber-700' },
  hors_service: { label: 'Hors service', class: 'bg-rose-50 text-rose-700' },
  en_maintenance: { label: 'En maintenance', class: 'bg-orange-50 text-orange-700' }
};

const ScanPage = () => {
  const navigate = useNavigate();
  const [scannerActive, setScannerActive] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [lastScannedCode, setLastScannedCode] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [scanCooldown, setScanCooldown] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [typeLabels, setTypeLabels] = useState({});
  const inputRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const scannerContainerId = 'html5qr-code-scanner';
  const COOLDOWN_MS = 1500; // 1.5 seconds cooldown

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    // Load furniture types for labels
    loadFurnitureTypes();
  }, []);

  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
      } catch (err) {
        console.log('Error stopping scanner:', err);
      }
      html5QrCodeRef.current = null;
    }
  }, []);

  // Start cooldown timer
  const startCooldown = useCallback(() => {
    setScanCooldown(true);
    setCooldownRemaining(COOLDOWN_MS);
    
    // Clear any existing timer
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    // Update remaining time every 100ms
    const startTime = Date.now();
    cooldownTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, COOLDOWN_MS - elapsed);
      setCooldownRemaining(remaining);
      
      if (remaining <= 0) {
        clearInterval(cooldownTimerRef.current);
        setScanCooldown(false);
        setCooldownRemaining(0);
      }
    }, 100);
  }, []);

  const handleScan = async (decodedText) => {
    if (!decodedText || decodedText === lastScannedCode || scanCooldown) return;
    
    setLastScannedCode(decodedText);
    setScanFeedback({ type: 'scanning', code: decodedText });
    
    // Play sound feedback
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 1200;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      oscillator.start();
      setTimeout(() => oscillator.stop(), 100);
    } catch (e) {
      // Audio not supported
    }
    
    await searchByBarcode(decodedText);
    
    // Start cooldown after scan
    startCooldown();
    
    setTimeout(() => setLastScannedCode(''), 2000);
  };

  const searchByBarcode = async (code) => {
    if (!code.trim() || scanCooldown) return;

    setLoading(true);
    setError('');
    setSearchResult(null);

    try {
      const response = await furnitureApi.getByBarcode(code.trim());
      setSearchResult(response.data);
      setScanFeedback({ type: 'success', code });

      setScanHistory(prev => {
        const newHistory = [
          { code: code.trim(), found: true, timestamp: new Date(), furniture: response.data },
          ...prev.slice(0, 9)
        ];
        return newHistory;
      });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Aucun mobilier trouvé avec ce code-barres');
        setScanFeedback({ type: 'notfound', code });
        setScanHistory(prev => {
          const newHistory = [
            { code: code.trim(), found: false, timestamp: new Date() },
            ...prev.slice(0, 9)
          ];
          return newHistory;
        });
      } else {
        setError('Erreur lors de la recherche');
        setScanFeedback({ type: 'error', code });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    searchByBarcode(manualInput);
    setManualInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && manualInput.trim()) {
      e.preventDefault();
      searchByBarcode(manualInput);
      setManualInput('');
    }
  };

  const startScanner = async (cameraId = null) => {
    setCameraError(null);
    setCameraReady(false);
    
    try {
      // Get available cameras
      const devices = await Html5Qrcode.getCameras();
      
      if (!devices || devices.length === 0) {
        setCameraError('Aucune caméra détectée sur cet appareil.');
        return;
      }
      
      setCameras(devices);
      console.log('Available cameras:', devices);
      
      // Detect platform
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isMobile = isIOS || isAndroid;
      
      // Select camera
      let cameraToUse = cameraId;
      if (!cameraToUse) {
        if (isMobile) {
          const rearCamera = devices.find(d => 
            d.label.toLowerCase().includes('back') || 
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('arrière') ||
            d.label.toLowerCase().includes('environment')
          );
          cameraToUse = rearCamera ? rearCamera.id : devices[0].id;
        } else {
          cameraToUse = devices[0].id;
        }
      }
      setSelectedCameraId(cameraToUse);
      console.log('Using camera:', cameraToUse);
      
      // Create scanner instance with optimized formats for 2D codes
      // Note: html5-qrcode has known issues with DATA_MATRIX (based on unmaintained ZXing.js)
      // Using native BarcodeDetector API when supported significantly improves 2D detection
      const formatsToSupport = [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.DATA_MATRIX,
        Html5QrcodeSupportedFormats.AZTEC,
        Html5QrcodeSupportedFormats.PDF_417,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E
      ];
      
      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId, { 
        formatsToSupport,
        verbose: false,
        // Use native BarcodeDetector API when available (much better for 2D codes)
        useBarCodeDetectorIfSupported: true
      });
      
      // Configuration optimisée pour codes 2D (QR, DataMatrix, Aztec)
      // - Zone carrée large pour mieux capturer les codes 2D
      // - FPS plus élevé pour une détection plus rapide
      const config = {
        fps: 20, // Higher FPS for faster detection
        qrbox: { width: 300, height: 300 }, // Larger square area for 2D codes
        aspectRatio: 1.0,
        disableFlip: false,
        // Enable native BarcodeDetector API on supported browsers (Chrome, Edge, Android)
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };
      
      // Camera config
      let cameraConfig;
      if (isIOS) {
        cameraConfig = { facingMode: "environment" };
      } else if (isAndroid) {
        // On Android, use deviceId for more reliable camera selection
        cameraConfig = cameraToUse;
      } else {
        // Desktop - use deviceId
        cameraConfig = cameraToUse;
      }
      
      console.log('Camera config:', cameraConfig);
      console.log('Scanner config:', config);
      console.log('Formats supported:', formatsToSupport.length, 'formats');
      
      await html5QrCodeRef.current.start(
        cameraConfig,
        config,
        (decodedText, decodedResult) => {
          console.log('Barcode detected:', decodedText, decodedResult);
          handleScan(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors - they happen when no barcode is in frame
        }
      );
      
      setCameraReady(true);
      console.log('Scanner started successfully');
      
    } catch (err) {
      console.error('Camera error:', err);
      let errorMessage = "Erreur d'accès à la caméra";
      
      const errorStr = String(err);
      
      if (errorStr.includes('NotAllowedError') || errorStr.includes('Permission')) {
        errorMessage = "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.";
      } else if (errorStr.includes('NotFoundError') || errorStr.includes('not found')) {
        errorMessage = 'Aucune caméra détectée sur cet appareil.';
      } else if (errorStr.includes('NotReadableError') || errorStr.includes('already in use')) {
        errorMessage = 'La caméra est utilisée par une autre application.';
      } else if (errorStr.includes('NotSupportedError') || errorStr.includes('https')) {
        errorMessage = 'La caméra nécessite une connexion sécurisée (HTTPS).';
      } else if (errorStr.includes('OverconstrainedError')) {
        errorMessage = 'La caméra ne supporte pas les paramètres demandés.';
      }
      
      setCameraError(errorMessage);
    }
  };

  const openScanner = async () => {
    setScanFeedback(null);
    setLastScannedCode('');
    setScannerActive(true);
    // Small delay to ensure DOM element is rendered
    setTimeout(() => startScanner(), 100);
  };

  const closeScanner = async () => {
    await stopScanner();
    setScannerActive(false);
    setScanFeedback(null);
    setCameraError(null);
    setCameraReady(false);
    setTorchOn(false);
  };

  const retryCamera = () => {
    setCameraError(null);
    setCameraReady(false);
    stopScanner().then(() => {
      setTimeout(() => startScanner(selectedCameraId), 100);
    });
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    const currentIndex = cameras.findIndex(c => c.id === selectedCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCameraId = cameras[nextIndex].id;
    
    await stopScanner();
    setTimeout(() => startScanner(nextCameraId), 100);
  };

  const toggleTorch = async () => {
    if (!html5QrCodeRef.current) return;
    
    try {
      const capabilities = html5QrCodeRef.current.getRunningTrackCameraCapabilities();
      if (capabilities && capabilities.torchFeature && capabilities.torchFeature.isSupported()) {
        await capabilities.torchFeature.apply(!torchOn);
        setTorchOn(!torchOn);
      }
    } catch (err) {
      console.log('Torch not supported:', err);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scan-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Scanner
        </h1>
        <p className="text-slate-500 mt-1">Scannez ou saisissez un code-barres pour rechercher un mobilier</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scan Options */}
        <div className="space-y-4">
          {/* Camera Scan Button */}
          <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <Smartphone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  Scanner avec la caméra
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Utilisez la caméra de votre appareil pour scanner les codes-barres et QR codes
                </p>
                <Button
                  onClick={openScanner}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="lg"
                  data-testid="camera-scan-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Ouvrir le scanner
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Manual Input */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-slate-400" />
                Saisie manuelle / Lecteur physique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualSearch} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="barcode">Code-barres</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      id="barcode"
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Scannez ou saisissez le code..."
                      className="font-mono flex-1"
                      autoComplete="off"
                      disabled={scanCooldown}
                      data-testid="barcode-input"
                    />
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={loading || !manualInput.trim() || scanCooldown}
                      data-testid="search-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      ) : (
                        <ScanBarcode className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  {scanCooldown ? (
                    <div className="flex items-center gap-2 text-xs text-amber-600">
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-amber-600 border-t-transparent" />
                      <span>Temporisation... {(cooldownRemaining / 1000).toFixed(1)}s</span>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      Connectez un lecteur de code-barres USB ou Bluetooth
                    </p>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Search Result */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Résultat</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-rose-50 text-rose-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {searchResult && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 text-emerald-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="font-medium">Mobilier trouvé</p>
                </div>

                <div className="p-4 rounded-lg border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-lg font-medium">{searchResult.code_barres}</span>
                    <Badge className={STATUS_CONFIG[searchResult.etat]?.class}>
                      {STATUS_CONFIG[searchResult.etat]?.label}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Type</p>
                      <p className="font-medium">{typeLabels[searchResult.type_mobilier] || searchResult.type_mobilier}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Marque</p>
                      <p className="font-medium">{searchResult.marque || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Modèle</p>
                      <p className="font-medium">{searchResult.modele || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Localisation</p>
                      <p className="font-medium">{searchResult.location_path || searchResult.site_nom || 'Non affecté'}</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate(`/furniture/${searchResult.id}`)}
                    className="w-full mt-4 bg-slate-900 hover:bg-slate-800"
                    data-testid="view-detail-btn"
                  >
                    Voir le détail
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {!error && !searchResult && (
              <div className="text-center py-8 text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Scannez un code-barres pour afficher les informations</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Historique des scans</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scanHistory.map((scan, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    scan.found ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                  onClick={() => scan.furniture && navigate(`/furniture/${scan.furniture.id}`)}
                >
                  <div className="flex items-center gap-3">
                    {scan.found ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-rose-600" />
                    )}
                    <div>
                      <span className="font-mono text-sm">{scan.code}</span>
                      {scan.furniture && (
                        <p className="text-xs text-slate-500">{scan.furniture.marque} {scan.furniture.modele}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {scan.timestamp.toLocaleTimeString('fr-FR')}
                    </span>
                    {scan.found && <ArrowRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Camera Scanner Modal */}
      <Dialog open={scannerActive} onOpenChange={(open) => !open && closeScanner()}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                Scanner automatique
              </span>
              <Button variant="ghost" size="icon" onClick={closeScanner}>
                <X className="w-5 h-5" />
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Utilisez votre caméra pour scanner un code-barres
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative bg-black" style={{ minHeight: '350px' }}>
            {cameraError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-slate-900 text-white">
                <CameraOff className="w-16 h-16 text-slate-400 mb-4" />
                <p className="text-center mb-4">{cameraError}</p>
                <div className="flex gap-3">
                  <Button onClick={retryCamera} variant="outline" className="text-white border-white hover:bg-white/10">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Réessayer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div id={scannerContainerId} style={{ width: '100%', minHeight: '350px' }}></div>
                
                {/* Camera Controls */}
                {cameraReady && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                    {cameras.length > 1 && (
                      <Button 
                        onClick={switchCamera} 
                        size="sm" 
                        variant="secondary"
                        className="bg-black/50 hover:bg-black/70 text-white"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Changer
                      </Button>
                    )}
                    <Button 
                      onClick={toggleTorch} 
                      size="sm" 
                      variant="secondary"
                      className="bg-black/50 hover:bg-black/70 text-white"
                    >
                      {torchOn ? (
                        <FlashlightOff className="w-4 h-4 mr-1" />
                      ) : (
                        <Flashlight className="w-4 h-4 mr-1" />
                      )}
                      Flash
                    </Button>
                  </div>
                )}
                
                {/* Loading state */}
                {!cameraReady && !cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-white">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mb-4"></div>
                    <p>Initialisation de la caméra...</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Scan Feedback Overlay */}
            {scanFeedback && !cameraError && cameraReady && (
              <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity ${
                scanFeedback.type === 'scanning' ? 'bg-blue-500/20' :
                scanFeedback.type === 'success' ? 'bg-emerald-500/20' :
                scanFeedback.type === 'notfound' ? 'bg-amber-500/20' :
                'bg-rose-500/20'
              }`}>
                <div className={`px-4 py-2 rounded-full text-white text-sm font-medium ${
                  scanFeedback.type === 'scanning' ? 'bg-blue-600' :
                  scanFeedback.type === 'success' ? 'bg-emerald-600' :
                  scanFeedback.type === 'notfound' ? 'bg-amber-600' :
                  'bg-rose-600'
                }`}>
                  {scanFeedback.type === 'scanning' && 'Recherche...'}
                  {scanFeedback.type === 'success' && 'Trouvé !'}
                  {scanFeedback.type === 'notfound' && 'Non trouvé'}
                  {scanFeedback.type === 'error' && 'Erreur'}
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t bg-slate-50">
            {!cameraError && (
              <Alert className="mb-3">
                <Camera className="h-4 w-4" />
                <AlertDescription>
                  <span className="block">Pointez la caméra vers un code-barres (Code 128, QR, DataMatrix, EAN...).</span>
                  <span className="text-xs text-slate-500">Assurez-vous d&apos;avoir un bon éclairage et tenez le code à environ 15-20 cm.</span>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={(e) => { e.preventDefault(); searchByBarcode(manualInput); setManualInput(''); }} className="flex gap-2">
              <Input
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="Ou saisissez manuellement..."
                className="font-mono"
              />
              <Button type="submit" disabled={!manualInput.trim() || loading}>
                {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : 'OK'}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScanPage;
